import React from "react";
import cx from "classnames";

import { Action, Category } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";

import MinusIcon from "components/Icons/minus.svg";
import PlusIcon from "components/Icons/plus.svg";

type QuantitySelectorProps = {
  className?: string;
  dispensaryId?: number;
  label?: string;
  inStoreCartEnabled?: boolean;
  max?: number;
  menuId?: number;
  min?: number;
  onChange: (e: number) => void;
  step?: number;
  tallSelector?: boolean;
  trackClickEvent?: () => void;
  trackImpressionEvent?: () => void;
  value: number;
};

const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  className,
  dispensaryId,
  label,
  inStoreCartEnabled,
  max = 99,
  menuId,
  min = 1,
  onChange,
  step = 1,
  tallSelector = false,
  trackClickEvent,
  value = 1,
}) => {
  const { publishEvent } = useEventTracker();

  const decrement = () => {
    if (!value) {
      onChange(step);
    } else if (value > min) {
      onChange(value - step);
    }
  };

  const increment = () => {
    if (!value) {
      onChange(step);
    } else if (value < max) {
      onChange(value + step);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const _onChange = (e: any) => {
    const { valueAsNumber } = e.target;
    if (
      /[0-9]/.test(valueAsNumber) &&
      valueAsNumber <= max &&
      valueAsNumber >= min
    ) {
      onChange(valueAsNumber);
    } else {
      onChange(Number.NaN);
    }
  };
  const onBlur = () => {
    if (!value) {
      onChange(step);
    }
  };

  const sharedEventProps = {
    category: Category.dispensaryMenuPdp,
    dispensaryId,
    menuId,
  };

  return (
    <>
      <div data-testid="parent" className={cx("w-full", className)}>
        <label className="pb-4 font-bold text-xs">{label}</label>
        <div
          className={cx(
            "flex flex-row rounded relative bg-white border border-grey-44",
            { "h-10 mt-1 md:w-80": !tallSelector, "h-[50px]": tallSelector },
          )}
        >
          <button
            onClick={async () => {
              const label = inStoreCartEnabled
                ? "qty decrease - shop every menu"
                : "qty decrease";

              trackClickEvent && trackClickEvent();

              publishEvent({
                action: Action.click,
                label,
                ...sharedEventProps,
              });

              decrement();
            }}
            data-testid="quantity-selector-decrement"
            className="text-green text-xl h-full rounded-l cursor-pointer focus:outline-none"
            aria-label={`Click to Decrease Quantity To ${value - step}`}
          >
            {value !== 1 ? (
              <MinusIcon className="ml-2" width="24" height="24" />
            ) : (
              <div className="p-md" />
            )}
          </button>
          <input
            {...{ max, min, step, value }}
            onChange={_onChange}
            onBlur={onBlur}
            type="number"
            className={cx(
              "outline-none focus:outline-none text-center w-full font-bold text-md focus:text-black md:text-basecursor-default flex items-center text-gray-700 border-transparent bg-transparent qselector-spinner-hide",
              { "text-sm": tallSelector },
            )}
            name="custom-input-number"
            data-testid="quantity-selector-value"
            aria-label={`Quantity of ${value}`}
          />
          <button
            onClick={async () => {
              const label = inStoreCartEnabled
                ? "qty increase - shop every menu"
                : "qty increase";

              trackClickEvent && trackClickEvent();

              publishEvent({
                action: Action.click,
                label,
                ...sharedEventProps,
              });

              increment();
            }}
            data-testid="quantity-selector-increment"
            className="h-full rounded-r cursor-pointer"
            aria-label={`Click to Increase Quantity To ${value + step}`}
          >
            <PlusIcon className="mr-2" width="24" height="24" />
          </button>
        </div>
      </div>
    </>
  );
};

export default QuantitySelector;
